export type DataTableEntityType =
  | "client"
  | "prospect"
  | "past_client"
  | "task"
  | "meeting"
  | "contact"
  | "project"
  | "business"
  | "custodian_account"
  | "manual_account"
  | "insurance_policy"
  | "account"
  | "trust"
  | "opportunity"
  | "email"
  | "email_message"
  | "person"
  | "intake_form_invitation"
  | "intake_form_submission"
  | "automation_email_message"
  | "report"
  | "service_request"
  | "schwab_activity"

export interface ViewColumn {
  id?: string
  data_view_id?: string
  name?: string
  field: string
  sort_order: number
  is_visible: boolean
  column_width?: number
}

export const DEFAULT_COLUMN_WIDTHS: Record<DataTableEntityType, Record<string, number>> & {
  default: Record<string, number>
} = {
  schwab_activity: {
    title: 330,
    household_name: 180,
    account_number: 130,
    category: 170,
    status: 165,
    priority: 100,
    assignment_group: 120,
    source_updated_at: 185,
  },
  service_request: {
    service_name: 240,
    record_name: 220,
    company_name: 180,
    owner_name: 180,
    status: 140,
    percent_complete: 190,
    next_due_on: 130,
    created_at: 140,
  },
  client: {
    id: 140,
    name: 280,
    email: 260,
    phone_number: 180,
    tier: 140,
    tags: 200,
    became_client_at: 180,
    created_at: 180,
    updated_at: 180,
    last_meeting_at: 180,
    next_meeting_at: 180,
    last_review_meeting_at: 200,
    next_review_meeting_at: 200,
    next_review_due_on: 180,
    last_touchpoint_at: 220,
    next_touchpoint_due_on: 180,
    effective_touchpoint_cadence: 180,
    country: 180,
    city: 140,
    state: 80,
    zip: 100,
    full_address: 280,
    estimated_income_range: 200,
    tax_id: 160,
    additional_info: 280,
  },
  prospect: {
    id: 140,
    name: 280,
    email: 260,
    phone_number: 180,
    status: 120,
    tags: 200,
    created_at: 180,
    updated_at: 180,
    last_meeting_at: 180,
    next_meeting_at: 180,
    last_review_meeting_at: 200,
    next_review_meeting_at: 200,
    last_touchpoint_at: 220,
    next_touchpoint_due_on: 180,
    effective_touchpoint_cadence: 180,
    country: 180,
    city: 140,
    state: 80,
    zip: 100,
    full_address: 280,
    estimated_income_range: 200,
    estimated_wealth_range: 180,
    home_ownership_status: 160,
    estimated_home_value: 160,
  },
  past_client: {
    name: 280,
    email: 260,
    phone_number: 180,
    tags: 200,
    became_past_client_at: 180,
    became_client_at: 180,
    created_at: 180,
    updated_at: 180,
    last_meeting_at: 180,
    next_meeting_at: 180,
    last_touchpoint_at: 220,
    next_touchpoint_due_on: 180,
    effective_touchpoint_cadence: 180,
    country: 180,
    city: 140,
    state: 80,
    zip: 100,
    full_address: 280,
  },
  contact: {
    "person.fullName": 280,
    "person.email": 260,
    "person.phoneNumber": 180,
    tags: 200,
    created_at: 180,
    last_touchpoint_at: 220,
    country: 180,
    city: 140,
    state: 80,
    zip: 100,
    full_address: 280,
  },
  task: {
    name: 280,
    status: 120,
    priority: 120,
    due_date: 180,
    created_at: 180,
    updated_at: 180,
  },
  meeting: {
    title: 280,
    start_time: 180,
    end_time: 180,
    created_at: 180,
  },
  project: {
    name: 280,
    "project_template.title": 200,
    assigned_to_id: 180,
    status: 120,
    milestone_progress: 200,
    overall_progress: 200,
    project_milestone_id: 180,
    due_date: 150,
    created_at: 180,
    milestone_updated_at: 180,
    completed_at: 180,
    created_by_id: 180,
    household: 220,
  },
  business: {
    name: 280,
    email: 260,
    phone_number: 180,
    website_url: 260,
    industry: 180,
    tags: 200,
    created_at: 180,
    updated_at: 180,
  },
  custodian_account: {
    account_name: 280,
    custodian: 180,
    account_type_normalized: 200,
    total_market_value: 180,
    masked_account_number: 180,
    primary_related_party_summary: 260,
    linked_household_name: 240,
    linked_business_name: 240,
  },
  manual_account: {
    name: 280,
    household_name: 200,
    account_type: 160,
    tax_advantaged_type: 180,
    institution_name: 180,
    balance_in_cents: 160,
    masked_account_number: 180,
  },
  insurance_policy: {
    name: 260,
    household_name: 220,
    insurance_type: 180,
    carrier: 200,
    masked_policy_number: 180,
    life_policy_type: 160,
    premium: 160,
    coverage_amount: 180,
    renewal_date: 160,
  },
  account: {
    name: 260,
    household_name: 220,
    record_type: 150,
    account_type: 190,
    institution: 200,
    amount: 180,
    masked_number: 180,
  },
  trust: {
    name: 280,
    household_name: 220,
    tax_id: 160,
    trust_type: 160,
    tax_classification: 180,
    execution_date: 160,
    governing_law: 260,
    trust_purpose: 220,
    funding_status: 160,
    distribution_terms: 320,
    amendment_or_restatement_date: 220,
    additional_info: 280,
    created_at: 180,
  },
  opportunity: {
    name: 280,
    value: 180,
    probability: 140,
    pipeline: 180,
    pipeline_stage: 180,
    target_close_date: 180,
    created_at: 180,
    updated_at: 180,
  },
  email: {
    subject: 320,
    from: 260,
    to: 260,
    date: 180,
    created_at: 180,
  },
  email_message: {},
  person: {
    full_name: 280,
    household_name: 200,
    email: 260,
    phone_number: 180,
    date_of_birth: 140,
    household_role: 160,
    "household.tier": 140,
    "household.tags": 200,
    linkedin_url: 220,
    job_title: 180,
    gender: 120,
    marital_status: 140,
    preferred_name: 160,
    employments: 220,
    "household.primary_education_institution_name": 220,
  },
  intake_form_invitation: {
    client: 240,
    intake_form: 240,
    portal_url: 180,
    state: 160,
    submitted_at: 160,
    created_at: 160,
  },
  intake_form_submission: {
    client: 240,
    intake_form_version: 240,
    source: 160,
    submitted_at: 160,
    applied: 160,
    document_count: 140,
  },
  automation_email_message: {},
  report: {},
  default: {
    name: 280,
    email: 260,
    phone_number: 180,
    tags: 200,
    status: 120,
    tier: 140,
    created_at: 180,
    updated_at: 180,
    country: 180,
    city: 140,
    state: 80,
    zip: 100,
    full_address: 280,
  },
}

export const DEFAULT_COLUMN_WIDTH_FALLBACK = 240

export const getDefaultColumnWidth = (entityType?: DataTableEntityType, fieldName?: string): number => {
  if (entityType && fieldName) {
    return (
      DEFAULT_COLUMN_WIDTHS[entityType]?.[fieldName] ||
      DEFAULT_COLUMN_WIDTHS.default[fieldName] ||
      DEFAULT_COLUMN_WIDTH_FALLBACK
    )
  }
  return DEFAULT_COLUMN_WIDTH_FALLBACK
}
